import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";

/**
 * Tooltip — the shared hover/focus hint for icon-only controls.
 *
 * Opens on hover AND on keyboard focus (`:focus-visible`, so a mouse click
 * doesn't leave it stuck open); closes on mouseleave, blur, or Escape.
 * A native `title` attribute is NOT an acceptable substitute — it never opens
 * on keyboard focus, so a keyboard user can reach the control and still be
 * told nothing about it.
 *
 * PORTALED TO <body>. An absolutely-positioned child would be clipped by the
 * flex / overflow containers these triggers live in (a page header's actions
 * row, a table cell, a card). Position comes from the trigger's viewport rect
 * and is recomputed on scroll (capture phase, so scrolling containers count)
 * and on resize while open.
 *
 * THE BUBBLE IS `aria-hidden` AND PURELY VISUAL — it is deliberately NOT an
 * `aria-describedby` target. The trigger already carries the same words in its
 * accessible name (`IconBtn` requires `label` and renders it to `aria-label`),
 * so:
 *   - assistive-technology users get the text from the name, and pointing at it
 *     with a description would announce the same string twice;
 *   - TOUCH users, who have no hover and may never focus the control, get it
 *     from that same name — the one channel that needs no pointer at all.
 * So the label passed here should MIRROR the trigger's `aria-label`; this
 * component adds a visual affordance, never a second source of information.
 *
 * Composition: the single child is CLONED and the handlers are merged onto it,
 * so the tooltip contributes NO DOM node of its own and cannot perturb the
 * layout of the row it sits in. The child must spread unknown props onto its
 * root element — `IconBtn`, `Button` and any intrinsic element do.
 *
 *   <Tooltip label="Clear filters">
 *     <IconBtn label="Clear filters" onClick={clear}><X size={16} /></IconBtn>
 *   </Tooltip>
 */
export type TooltipPlacement = "top" | "bottom";

/** The handler subset the tooltip merges onto its child. */
interface TriggerHandlers {
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
}

export interface TooltipProps {
  /** Visible hint. Mirror the trigger's `aria-label`; blank renders nothing. */
  label: string;
  /** Exactly one element that spreads unknown props onto its root node. */
  children: React.ReactElement;
  /** Preferred side. Flips to the other side only when it would not fit. */
  placement?: TooltipPlacement;
  /** Extra classes for the bubble itself (not the trigger). */
  className?: string;
}

/**
 * `:focus-visible` in `matches()` throws on a few pre-2022 engines. A hint that
 * opens on every focus beats a focus handler that throws inside the trigger.
 */
function isFocusVisible(el: Element): boolean {
  try {
    return el.matches(":focus-visible");
  } catch {
    return true;
  }
}

export function Tooltip({
  label,
  children,
  placement = "top",
  className,
}: TooltipProps) {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const tipRef = React.useRef<HTMLDivElement | null>(null);

  const open = label.trim().length > 0 && (hovered || focused);

  const reposition = React.useCallback(() => {
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;
    const rect = trigger.getBoundingClientRect();
    // The positioned node carries the gap as vertical padding, so its measured
    // height already includes it — no pixel constant needed here.
    const width = tip.offsetWidth;
    const height = tip.offsetHeight;
    const fitsAbove = rect.top - height >= 0;
    const fitsBelow = rect.bottom + height <= window.innerHeight;
    const above =
      placement === "top" ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;
    setPos({
      top: above ? rect.top - height : rect.bottom,
      left: Math.max(
        0,
        Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width),
      ),
    });
  }, [placement]);

  // Measured before paint, so the first frame is already in the right place.
  React.useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onViewportChange = () => reposition();
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);
    return () => {
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
    };
  }, [open, reposition]);

  const close = React.useCallback(() => {
    setHovered(false);
    setFocused(false);
  }, []);

  // Escape dismisses the hint. Document-level because it can be open on hover
  // alone, with focus somewhere else entirely.
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  const child = React.Children.only(children) as React.ReactElement<TriggerHandlers>;
  const childProps = child.props;

  const trigger = React.cloneElement(child, {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      triggerRef.current = e.currentTarget;
      setHovered(true);
      childProps.onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      setHovered(false);
      childProps.onMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      triggerRef.current = e.currentTarget;
      if (isFocusVisible(e.currentTarget)) setFocused(true);
      childProps.onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      setFocused(false);
      childProps.onBlur?.(e);
    },
  });

  return (
    <>
      {trigger}
      {open &&
        createPortal(
          <div
            ref={tipRef}
            aria-hidden
            data-phx-tooltip=""
            style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
            // Above the modal layer (z-50) and the toast layer (z-60): a hint
            // on a control inside a dialog still has to paint over it.
            // `pointer-events-none` keeps the bubble from ever stealing the
            // hover it was opened by. `invisible` (not `hidden`) for the
            // pre-measure frame, so it still has a box to measure.
            className={cn(
              "fixed z-[70] py-space-1 pointer-events-none",
              !pos && "invisible",
            )}
          >
            <span
              className={cn(
                "block max-w-xs rounded-md bg-onyx px-space-2 py-space-1",
                "text-caption text-white-pure shadow-lg",
                className,
              )}
            >
              {label}
            </span>
          </div>,
          document.body,
        )}
    </>
  );
}
